<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({name: "ComponentMergeAccountInfoDialog"});

  const pageProperties = usePage().props;
  const refDialog = ref(null);
  const transferUrl = ref(null);

  const status = {
    NOT_AVAILABLE: "not_available",
    ALREADY_CONNECTED: "already_connected",
    NOT_CONNECTED_P4U_TOKEN: "not_connected_p4u_token",
    NOT_CONNECTED_NID_TOKEN: "not_connected_nid_token",
    NOT_CONNECTED_MC_TOKEN: "not_connected_mc_token",
    EXISTING_TRANSFER: "existing_transfer",
  };

  const currentStatus = computed(() => {
    if (transferUrl.value !== null) {
      return status.EXISTING_TRANSFER;
    }

    if (pageProperties.can.is_education) {
      return status.NOT_AVAILABLE;
    }

    if (pageProperties.client.license.license_name === "connect-pro") {
      return status.ALREADY_CONNECTED;
    }

    if (pageProperties.can.has_p4u_token) {
      return status.NOT_CONNECTED_P4U_TOKEN;
    }

    if (pageProperties.can.has_nident_token) {
      return status.NOT_CONNECTED_NID_TOKEN;
    }

    if (pageProperties.can.has_medicheck_token) {
      return status.NOT_CONNECTED_MC_TOKEN;
    }

    return null;
  });

  const dialogTitle = computed(() => {
    if (is(status.ALREADY_CONNECTED)) {
      return "Ihre Accounts sind erfolgreich verknüpft";
    }
    return "Accounts verknüpfen";
  });

  function is(status) {
    return status === currentStatus.value;
  }

  function open() {
    // load existing transfer (created from support via nova backend)
    useAuthHttp().get(route("merge-account.index"), {
      onSuccess: (response) => {
        if (response?.url) {
          transferUrl.value = response.url;
        }
      },
    });

    // load information for existing (connected) third parties
    router.reload({
      only: ["allThirdParties"],
      onSuccess: () => {
        refDialog.value.open();
      },
    });
  }

  defineExpose({open});
</script>

<template>
  <component-dialog
    ref="refDialog"
    dialog-type="information"
    :dialog-title="dialogTitle"
    dialog-width="medium"
    :test-id="getTestId('merge-account')"
  >
    <!-- 🧑‍💻 support user has created a merge account transfer object -->
    <template v-if="is(status.EXISTING_TRANSFER)" #content>
      <h6 class="font-semibold">Die Verknüpfung Ihres Accounts ist vorbereitet</h6>
      <p class="mt-2">
        Sie können den Vorgang durch Klick auf „Verknüpfung starten“ beginnen. Dort können Sie alle Informationen vorab
        prüfen, bevor Sie den Vorgang endgültig starten.
      </p>
    </template>

    <!-- ✔️ account merging is not available -->
    <template v-else-if="is(status.NOT_AVAILABLE)" #content>
      <h6 class="font-semibold">Ihre MediCheck Lizenz kann nicht mit einer Warenwirtschaft verknüpft werden.</h6>
      <p class="mt-2">
        Als Nutzer von bspw. MediCheck Education steht Ihnen diese Funktion leider nicht zur Verfügung.
      </p>
    </template>

    <!-- ✔️ already connected => show status page -->
    <template v-else-if="is(status.ALREADY_CONNECTED)" #content>
      <div class="flex items-end space-x-4">
        <component-icon custom="front-desk" />
        <component-icon custom="connected" />
        <component-icon custom="laptop" />
      </div>

      <p class="mt-4">
        Ihre Accounts
        <span v-if="$page.props?.allThirdParties?.data" class="font-semibold">
          {{
            $page.props.allThirdParties.data
              .map((item) => `${item.provider ?? (item.type === "p4u" ? "pharma4u" : "N-Ident")} (${item.tp_id})`)
              .join(", ")
          }}
        </span>
        sind über eine gemeinsame Patientenübersicht verknüpft. Diese ist über ein gemeinsames Datenschutzpasswort
        erreichbar.
      </p>
    </template>

    <template v-else #content>
      <p>
        Mit dieser Funktion können Sie Ihren pharma4u-Account mit Ihrem Warenwirtschaftsaccount verknüpfen. Dadurch
        erhalten Sie eine gemeinsame Patientenübersicht, auf die Sie sowohl über Ihre Warenwirtschaft als auch über
        Ihren pharma4u-Login zugreifen können.
      </p>

      <!-- ✔️ not yet connected, with p4u token -->
      <p v-if="is(status.NOT_CONNECTED_P4U_TOKEN)" class="mt-4 font-semibold">
        Um die Verknüpfung zu starten, springen Sie bitte über Ihre Warenwirtschaft in den MediCheck ein und rufen Sie
        diesen Dialog erneut auf.
      </p>

      <!-- not yet connected, with nident or medicheck token -->
      <!-- p v-else-if="is(status.NOT_CONNECTED_NID_TOKEN) || is(status.NOT_CONNECTED_MC_TOKEN)"></p -->

      <p class="mt-4">
        Sollte dieser Fall nicht zutreffen, können Sie sich für weitere Verknüpfungsanfragen an unseren Kundensupport
        wenden:
        <br />
        <MedLink
          variant="primary"
          href="mailto:medicheck@pharma4u.de"
          :data-test="getTestId('merge-account-dialog-email-link')"
        >
          medicheck@pharma4u.de
        </MedLink>
        <br />
        <MedLink variant="primary" href="tel:+4961962025580" :data-test="getTestId('merge-account-dialog-phone-link')">
          06196 / 202558-0
        </MedLink>
      </p>
    </template>

    <template #actions>
      <template v-if="is(status.NOT_CONNECTED_NID_TOKEN) || is(status.NOT_CONNECTED_MC_TOKEN)">
        <MedButton
          as="inertia"
          :href="$route('merge-account.create')"
          variant="primary"
          :data-test="getTestId('merge-account-dialog-create-button')"
          @click="$refs.refDialog.close()"
        >
          Verknüpfung starten
        </MedButton>
      </template>

      <template v-else-if="is(status.EXISTING_TRANSFER)">
        <MedButton
          as="inertia"
          variant="primary"
          :href="transferUrl"
          :data-test="getTestId('merge-account-dialog-open-transfer-url-button')"
          @click="$refs.refDialog.close()"
        >
          Verknüpfung starten
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('merge-account-dialog-cancel-button')" @click="$refs.refDialog.close()">
        Abbrechen
      </MedButton>
    </template>
  </component-dialog>
</template>
